/**
 * Small English word lists shared by the `style-checkers-a` tools.
 *
 * There is no part-of-speech tagger here, so "is this word a verb?" is answered from a
 * list: auxiliaries and modals, a few hundred common verbs (with their irregular forms
 * spelled out), and regular endings generated from the base. The list is deliberately
 * biased toward verbs that are not also everyday nouns, because a checker that mistakes a
 * noun for a verb invents clauses that are not there.
 */

const RE_NT = /n't$/;
const RE_SIBILANT_END = /(?:s|x|z|ch|sh|o)$/;
const RE_CONSONANT_Y = /[^aeiou]y$/;
const RE_E_END = /e$/;

/** Forms of "to be", plus "am" and the contracted negatives, as lowercase words. */
export const BE_FORMS: ReadonlySet<string> = new Set([
  "am",
  "is",
  "are",
  "was",
  "were",
  "be",
  "been",
  "being",
]);

/** Finite auxiliaries and modals (the verb a clause needs in order to be a clause). */
export const AUXILIARIES: ReadonlySet<string> = new Set([
  "am",
  "is",
  "are",
  "was",
  "were",
  "has",
  "have",
  "had",
  "do",
  "does",
  "did",
  "will",
  "would",
  "can",
  "could",
  "may",
  "might",
  "must",
  "shall",
  "should",
  "ought",
  "ain't",
  "cannot",
]);

/** Contracted negatives and contractions that carry a finite auxiliary. */
const CONTRACTION_AUX = new Set([
  "i'm",
  "i've",
  "i'd",
  "i'll",
  "you're",
  "you've",
  "you'd",
  "you'll",
  "he'd",
  "he'll",
  "she'd",
  "she'll",
  "it'll",
  "it'd",
  "we're",
  "we've",
  "we'd",
  "we'll",
  "they're",
  "they've",
  "they'd",
  "they'll",
  "won't",
  "can't",
  "shan't",
]);

/** Irregular verbs: base followed by every finite or participial form. */
const IRREGULAR =
  "go goes went gone|say says said|make makes made|take takes took taken|see sees saw seen|" +
  "come comes came|get gets got gotten|give gives gave given|find finds found|know knows knew known|" +
  "think thinks thought|tell tells told|become becomes became|show shows showed shown|" +
  "leave leaves left|feel feels felt|bring brings brought|begin begins began begun|keep keeps kept|" +
  "hold holds held|write writes wrote written|stand stands stood|hear hears heard|let lets|" +
  "mean means meant|set sets|meet meets met|run runs ran|pay pays paid|sit sits sat|" +
  "speak speaks spoke spoken|lead leads led|read reads|grow grows grew grown|lose loses lost|" +
  "fall falls fell fallen|send sends sent|build builds built|understand understands understood|" +
  "draw draws drew drawn|break breaks broke broken|spend spends spent|cut cuts|rise rises rose risen|" +
  "drive drives drove driven|buy buys bought|wear wears wore worn|choose chooses chose chosen|" +
  "seek seeks sought|throw throws threw thrown|catch catches caught|deal deals dealt|win wins won|" +
  "forget forgets forgot forgotten|lay lays laid|eat eats ate eaten|teach teaches taught|" +
  "sell sells sold|fight fights fought|hang hangs hung|hit hits|put puts|shake shakes shook shaken|" +
  "steal steals stole stolen|sing sings sang sung|swim swims swam swum|fly flies flew flown|" +
  "ride rides rode ridden|hide hides hid hidden|bear bears bore borne|beat beats|bend bends bent|" +
  "bind binds bound|bite bites bit bitten|blow blows blew blown|burn burns burnt|cost costs|" +
  "dig digs dug|feed feeds fed|forbid forbids forbade forbidden|forgive forgives forgave forgiven|" +
  "freeze freezes froze frozen|lend lends lent|lie lies lay lain|light lights lit|ring rings rang rung|" +
  "shoot shoots shot|shut shuts|sleep sleeps slept|slide slides slid|spread spreads|strike strikes struck|" +
  "swear swears swore sworn|tear tears tore torn|wake wakes woke woken|weave weaves wove woven|" +
  "withdraw withdraws withdrew withdrawn|undergo undergoes underwent undergone|" +
  "overcome overcomes overcame|undertake undertakes undertook undertaken|" +
  "outperform outperforms outperformed|underlie underlies underlay underlain";

/** Regular verbs (base form), mostly academic and everyday actions that are not nouns. */
const REGULAR =
  "accept achieve acknowledge acquire act adapt add address adjust admit adopt advise affect agree " +
  "aim allow alter analyse analyze announce answer anticipate appear apply approach argue arise " +
  "arrive ask assert assess assign assist assume attempt attend attract avoid base believe belong " +
  "benefit calculate call care carry cause challenge change claim clarify combine commit compare " +
  "complain complete comprise conclude conduct confirm connect consider consist constitute construct " +
  "contain continue contradict contribute control convince cooperate correlate create decide declare " +
  "decline define delay demand demonstrate deny depend derive describe design determine develop " +
  "differ discover discuss display disagree dispute distinguish divide doubt dominate eliminate " +
  "emerge emphasise emphasize employ enable encourage end engage enhance ensure enter establish " +
  "estimate evaluate examine exceed exist expand expect experience explain explore express extend " +
  "face fail favor favour finish focus follow form generate happen help highlight hope identify " +
  "illustrate imply impose improve include increase indicate influence inform insist install " +
  "intend interpret introduce invest investigate involve join justify learn like limit link listen " +
  "live look love maintain manage matter measure mention minimise minimize miss move need note " +
  "notice observe obtain occur offer open operate oppose order organise organize overlook " +
  "participate perform permit persist play point possess predict prefer prepare present prevent " +
  "proceed produce promise propose protect prove provide publish pursue question raise reach " +
  "realise realize receive recognise recognize recommend record reduce reflect refuse regard " +
  "reject relate rely remain remember remove repeat replace reply report represent require " +
  "research resist respond restrict result retain return reveal review satisfy score seem select " +
  "separate serve share shift sign solve start state stay stop struggle study succeed suffer " +
  "suggest supply support suppose suspect talk tend test thank threaten treat try turn undermine " +
  "unite update use utilise utilize vary view visit wait walk want warn watch wonder work worry " +
  "yield laugh smile cry shout whisper nod frown stare wander arrive hurry open close enjoy " +
  "dislike hate mind notice cook clean paint call phone text email knock slam climb jump " +
  "kick push pull wash kiss hug greet invite join marry divorce move rent own owe " +
  "rain snow fit cancel cool warm drop fix pass fill gain lack rank rest rule sound smell taste " +
  "cover collapse decrease drift double fade fluctuate gather grab handle harm head ignore " +
  "inspect interview issue label launch lift lower mark match merge name pick plan post pose " +
  "praise print process publish qualify quote rate rank rush sample scan schedule settle shrink " +
  "skip slow spot stress strengthen submit surprise survive switch tackle target track trade " +
  "train transfer translate trigger trust verify vote weigh welcome widen withhold";

function regularForms(base: string): string[] {
  const forms = [base];
  if (RE_SIBILANT_END.test(base)) {
    forms.push(`${base}es`);
  } else if (RE_CONSONANT_Y.test(base)) {
    forms.push(`${base.slice(0, -1)}ies`);
  } else {
    forms.push(`${base}s`);
  }
  if (RE_E_END.test(base)) {
    forms.push(`${base}d`);
  } else if (RE_CONSONANT_Y.test(base)) {
    forms.push(`${base.slice(0, -1)}ied`);
  } else {
    forms.push(`${base}ed`, `${base}${base.at(-1)}ed`);
  }
  return forms;
}

function build(): {
  finite: Set<string>;
  base: Set<string>;
  participles: Set<string>;
  byBase: Map<string, string[]>;
} {
  const finite = new Set<string>();
  const base = new Set<string>();
  const participles = new Set<string>();
  const byBase = new Map<string, string[]>();
  for (const group of IRREGULAR.split("|")) {
    const [stem, ...forms] = group.split(" ");
    base.add(stem);
    finite.add(stem);
    byBase.set(stem, [stem, ...forms]);
    // A third form that differs from the past ("shown", "written") is a participle only;
    // it is not a finite verb on its own.
    const participleOnly = forms.length >= 3 && forms[2] !== forms[1];
    for (const [i, form] of forms.entries()) {
      if (!(participleOnly && i === 2)) {
        finite.add(form);
      }
    }
    // The last listed form is the participle; verbs listed with one form ("put", "set")
    // use the base for both.
    participles.add(forms.length >= 2 ? (forms.at(-1) ?? stem) : stem);
  }
  for (const stem of REGULAR.split(" ")) {
    base.add(stem);
    byBase.set(stem, regularForms(stem));
    for (const form of regularForms(stem)) {
      finite.add(form);
    }
    participles.add(`${stem}${RE_E_END.test(stem) ? "d" : "ed"}`);
    participles.add(`${stem}${stem.at(-1)}ed`);
  }
  return { finite, base, participles, byBase };
}

const BUILT = build();

/** Every base, third-person and past form in the lists. */
export const VERB_FORMS: ReadonlySet<string> = BUILT.finite;
/** Base forms only ("study", "show"). */
export const VERB_BASES: ReadonlySet<string> = BUILT.base;
/** Past participles ("studied", "shown", "written"). */
export const PARTICIPLES: ReadonlySet<string> = BUILT.participles;

/** Every listed form of each given base verb ("show" → show, shows, showed, shown). */
export function formsOf(bases: Iterable<string>): Set<string> {
  const out = new Set<string>();
  for (const base of bases) {
    for (const form of BUILT.byBase.get(base) ?? regularForms(base)) {
      out.add(form);
    }
  }
  return out;
}

/** Pronouns that can be the subject of a clause: personal and indefinite. */
export const SUBJECT_PRONOUNS: ReadonlySet<string> = new Set([
  "i",
  "you",
  "he",
  "she",
  "it",
  "we",
  "they",
  "nobody",
  "everybody",
  "everyone",
  "someone",
  "somebody",
  "anyone",
  "anybody",
  "nothing",
  "everything",
  "something",
  "anything",
]);

export const DETERMINERS: ReadonlySet<string> = new Set([
  "the",
  "a",
  "an",
  "my",
  "your",
  "his",
  "her",
  "its",
  "our",
  "their",
  "this",
  "that",
  "these",
  "those",
  "some",
  "many",
  "most",
  "each",
  "every",
  "no",
  "all",
  "several",
  "both",
  "another",
  "such",
  "any",
  "few",
  "one",
  "two",
  "three",
  "four",
  "five",
]);

/** Words that, directly before a word, make it a noun or adjective rather than a verb. */
export const NOUN_SIGNALS: ReadonlySet<string> = new Set([
  ...DETERMINERS,
  "of",
  "in",
  "on",
  "at",
  "by",
  "for",
  "with",
  "from",
  "about",
  "into",
  "over",
  "under",
  "between",
  "among",
  "than",
  "to",
  "and",
  "or",
]);

export const COORDINATORS: ReadonlySet<string> = new Set([
  "for",
  "and",
  "nor",
  "but",
  "or",
  "yet",
  "so",
]);

/** Words that open a dependent clause or an introductory phrase: a comma after them is correct. */
export const SUBORDINATORS: ReadonlySet<string> = new Set([
  "after",
  "although",
  "as",
  "because",
  "before",
  "if",
  "once",
  "since",
  "though",
  "unless",
  "until",
  "when",
  "whenever",
  "whereas",
  "while",
  "whether",
  "where",
  "wherever",
  "who",
  "whom",
  "whose",
  "which",
  "what",
  "why",
  "how",
  "whoever",
  "whatever",
  "provided",
  "supposing",
  "given",
  "despite",
  "unlike",
]);

export const PREPOSITIONS: ReadonlySet<string> = new Set([
  "about",
  "above",
  "across",
  "against",
  "along",
  "among",
  "around",
  "at",
  "before",
  "behind",
  "below",
  "beside",
  "between",
  "beyond",
  "by",
  "during",
  "except",
  "from",
  "in",
  "inside",
  "into",
  "near",
  "of",
  "off",
  "on",
  "onto",
  "outside",
  "over",
  "per",
  "through",
  "throughout",
  "to",
  "toward",
  "towards",
  "under",
  "until",
  "up",
  "upon",
  "with",
  "within",
  "without",
]);

/** Adverbs that may sit between a subject and its verb. */
export const MID_ADVERBS: ReadonlySet<string> = new Set([
  "also",
  "never",
  "always",
  "often",
  "just",
  "still",
  "soon",
  "then",
  "now",
  "even",
  "only",
  "really",
  "already",
  "usually",
  "sometimes",
  "rarely",
  "almost",
  "barely",
  "simply",
  "probably",
  "perhaps",
  "not",
  "too",
  "once",
  "again",
  "ever",
  "both",
  "all",
  "quickly",
  "slowly",
  "later",
]);

/** Verbs that report speech or thought: "…, he said, …" is a parenthetical, not a clause. */
export const ATTRIBUTION_VERBS: ReadonlySet<string> = new Set([
  "say",
  "says",
  "said",
  "think",
  "thinks",
  "thought",
  "believe",
  "believes",
  "believed",
  "suppose",
  "guess",
  "know",
  "mean",
  "argue",
  "argues",
  "argued",
  "note",
  "notes",
  "noted",
  "claim",
  "claims",
  "claimed",
  "add",
  "adds",
  "added",
  "wrote",
  "writes",
  "write",
  "explain",
  "explains",
  "explained",
  "reply",
  "replied",
  "observe",
  "observes",
  "observed",
  "admit",
  "admits",
  "admitted",
  "report",
  "reports",
  "reported",
  "suggest",
  "suggests",
  "suggested",
  "feel",
  "feels",
  "felt",
  "recall",
  "recalled",
  "remember",
  "remarked",
  "insisted",
  "concluded",
  "assume",
  "hope",
  "imagine",
  "trust",
  "warn",
  "warned",
]);

/** "Don't" → "do", "won't" → "will": the word a contraction stands on. */
export function uncontract(lower: string): string {
  if (lower === "won't") {
    return "will";
  }
  if (lower === "can't") {
    return "can";
  }
  if (lower === "shan't") {
    return "shall";
  }
  if (lower === "ain't") {
    return "am";
  }
  return RE_NT.test(lower) ? lower.slice(0, -3) : lower;
}

/** A modal or auxiliary, including contracted ones ("isn't", "I'm", "they'll"). */
export function isAuxiliary(lower: string): boolean {
  return (
    AUXILIARIES.has(lower) ||
    CONTRACTION_AUX.has(lower) ||
    AUXILIARIES.has(uncontract(lower))
  );
}

/** Any word the verb lists know, in a finite form, or an auxiliary. */
export function isFiniteVerb(lower: string): boolean {
  return isAuxiliary(lower) || VERB_FORMS.has(lower);
}
